import React, { useEffect } from 'react';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, Target, Users } from 'lucide-react';

const Lessons = () => {
  useEffect(() => {
    document.title = 'Golf Lessons - Pin High Golf & Lounge | PGA Pros in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional golf instruction with PGA-certified pros at Pin High Bay Village. Foresight technology, swing analysis, club fitting, and personalized coaching for all skill levels. Book your lesson today!');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/lessons');
    updateSocialMeta({
      title: 'Golf Lessons - Pin High Golf & Lounge | PGA Pros in Bay Village',
      description: 'Professional golf instruction with PGA-certified pros at Pin High Bay Village. Foresight technology, swing analysis, club fitting, and personalized coaching for all skill levels. Book your lesson today!',
      path: '/lessons',
    });
  }, []);

  const instructors = [
    {
      name: 'Matt Stroud',
      credentials: 'CLASS A PGA MEMBER',
      image: '/instructors/matt-stroud.png',
      email: 'mstroud@avonoakscc.com',
    },
    {
      name: 'Janice Moodie',
      credentials: 'LPGA TOUR CHAMPION',
      image: '/instructors/janice-moodie.png',
      email: 'jmoodie24@icloud.com',
    },
    {
      name: 'Erin Craig',
      credentials: 'CLASS A PGA MEMBER',
      image: '/instructors/erin-craig.png',
      email: 'ecraig@avonoakscc.com',
    },
    {
      name: 'Eric Oakes',
      credentials: 'PGA ASSOCIATE',
      image: '/instructors/eric-oaks.png',
      email: 'eoakes@avonoakscc.com',
    },
    {
      name: 'Austin Masturzo',
      credentials: 'PGA ASSOCIATE',
      image: '/instructors/austin-masturzo.png',
      email: 'amasturzo@avonoakscc.com',
    },
    {
      name: 'Mike Miller',
      credentials: 'LEVEL TWO PGA ASSOCIATE',
      image: '/instructors/mike-miller.png',
      email: 'mike.miller@chasegrouprealestate.com',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/lessons-hero.jpg"
            alt="Professional golf instruction at Pin High Bay Village"
            className="w-full h-full object-cover opacity-90"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <GraduationCap className="w-20 h-20 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            LESSONS WITH A LOCAL PRO
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            Sharpen your swing, fine-tune your short game, and elevate your confidence with lessons from our local golf professionals.
          </p>
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-gray-300">
            Whether you're a beginner learning the basics or an experienced player looking to analyze your technique, our pros use advanced simulator technology to give you real-time feedback and personalized instruction.
          </p>
        </div>
      </section>

      {/* Instructors */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Meet The Pros
            </h2>
          </div>

          {/* Start Your Journey Section */}
          <div className="max-w-4xl mx-auto mb-12 p-8 bg-white rounded-lg shadow-lg border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Start Your Journey</h3>
            <p className="text-gray-700 mb-4">
              Your perfect partnership is just a click away! Select the 'Connect' button to introduce yourself to your Pro.
            </p>
            <p className="text-gray-600 text-sm">
              Don't hold back! Use this opportunity to tell them all about you—your goals, your challenges, and the specific outcomes you are looking for. The more detail you provide, the better they can understand your unique situation and help you succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {instructors.map((instructor, index) => (
              <Card key={index} className="border-none shadow-xl hover:shadow-2xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-4 border-green-600">
                      <img
                        src={instructor.image}
                        alt={instructor.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{instructor.name}</h3>
                    <p className="text-green-600 font-semibold mb-6 text-sm">{instructor.credentials}</p>
                    <Button 
                      asChild
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <a href={`mailto:${instructor.email}`}>CONNECT</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Perfect your form, practice year-round, and bring your best game to the course.
          </h2>
        </div>
      </section>
    </div>
  );
};

export default Lessons;